// Live's project dialogs (spec A3b, design-ref/folders-410/06-08): "Move project", "Delete
// project" with its two choices, and "Select target project" after "Delete project but keep
// assets". The pickers are the platform's FolderPicker, as live's share one picker.
import { Box, Button, Group, Modal, Radio, Stack, Text } from "@mantine/core";
import { useState } from "react";

import { useDeleteFolder } from "../../api/generated/endpoints/assets/assets";
import { useMoveFolder } from "../../api/generated/endpoints/teams/teams";
import type { Folder, FolderContents } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { FolderPicker } from "../../platform/folders/FolderPicker";
import { selfAndDescendants, type FolderTree } from "../../platform/folders/folder-tree";

interface FolderDialogProps {
  teamId: string;
  brandId: string;
  tree: FolderTree;
  /** The folder the dialog is about; `undefined` closes it. */
  folder: Folder | undefined;
  onClose: () => void;
}

/** Live's "Move project": the picker with the folder and every folder under it refused. */
export function MoveFolderDialog(props: FolderDialogProps & { onMoved: () => Promise<void> }) {
  const { teamId, brandId, tree, folder, onClose, onMoved } = props;
  const move = useMoveFolder({
    mutation: {
      onSuccess: async () => {
        await onMoved();
        onClose();
      },
    },
  });
  const close = () => {
    move.reset();
    onClose();
  };
  return (
    <Modal opened={folder !== undefined} onClose={close} title="Move project" centered size={400}>
      {folder === undefined ? null : (
        <Stack gap="md">
          <FolderPicker
            teamId={teamId}
            brandId={brandId}
            submitLabel="Move"
            disabledIds={selfAndDescendants(tree, folder.id)}
            pending={move.isPending}
            onSubmit={(parentId) =>
              move.mutate({ teamId, brandId, folderId: folder.id, data: { parent_id: parentId } })
            }
            onCancel={close}
          />
          {move.isError ? <ErrorAlert error={move.error} /> : null}
        </Stack>
      )}
    </Modal>
  );
}

/** Live's two choices, in its words (DELETE_BRAND_PROJECT_MODE_OPTIONS, folders-410/07). */
const CHOICES: readonly { value: FolderContents; label: string; subtitle: string }[] = [
  {
    value: "delete",
    label: "Delete the project and all contents",
    subtitle: "Deletes all assets within the project, including those in nested subprojects.",
  },
  {
    value: "keep",
    label: "Delete project but keep assets",
    subtitle:
      "User must choose the destination project (or brand-level location) where all assets will be stored.",
  },
];

/**
 * Live's "Delete project": "Delete the project and all contents" (chosen first) deletes at once;
 * "Delete project but keep assets" asks for the destination in "Select target project" first.
 * `onDeleted` hears which folders went, so the library leaves one it was showing.
 */
export function DeleteFolderDialog(
  props: FolderDialogProps & { onDeleted: (gone: readonly string[]) => Promise<void> },
) {
  const { teamId, brandId, tree, folder, onClose, onDeleted } = props;
  const [contents, setContents] = useState<FolderContents>("delete");
  const [choosingTarget, setChoosingTarget] = useState(false);
  const remove = useDeleteFolder();
  const close = () => {
    remove.reset();
    setContents("delete");
    setChoosingTarget(false);
    onClose();
  };
  const run = (chosen: FolderContents, target: string | null) => {
    if (folder === undefined) return;
    const gone = chosen === "delete" ? selfAndDescendants(tree, folder.id) : [folder.id];
    remove.mutate(
      {
        teamId,
        brandId,
        folderId: folder.id,
        params: { contents: chosen, ...(target === null ? {} : { target }) },
      },
      {
        onSuccess: async () => {
          await onDeleted(gone);
          close();
        },
      },
    );
  };
  return (
    <>
      <Modal
        opened={folder !== undefined && !choosingTarget}
        onClose={close}
        title="Delete project"
        centered
        size={400}
      >
        <Stack gap="md">
          <Text size="sm" c="var(--cai-color-text-muted)">
            Choose options
          </Text>
          <Radio.Group
            value={contents}
            onChange={(value) => setContents(value as FolderContents)}
            aria-label="Choose options"
          >
            <Stack gap={8}>
              {CHOICES.map((choice) => (
                <Radio.Card key={choice.value} value={choice.value} radius="md" p="sm">
                  <Group wrap="nowrap" align="flex-start" gap="sm">
                    <Radio.Indicator mt={2} />
                    <Box>
                      <Text size="sm" fw={500}>
                        {choice.label}
                      </Text>
                      <Text size="xs" c="var(--cai-color-text-muted)">
                        {choice.subtitle}
                      </Text>
                    </Box>
                  </Group>
                </Radio.Card>
              ))}
            </Stack>
          </Radio.Group>
          {remove.isError && !choosingTarget ? <ErrorAlert error={remove.error} /> : null}
          <Group gap={8}>
            <Button
              color="red"
              loading={remove.isPending && contents === "delete"}
              onClick={() => (contents === "keep" ? setChoosingTarget(true) : run("delete", null))}
            >
              Delete
            </Button>
            <Button variant="subtle" color="gray" onClick={close}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal
        opened={folder !== undefined && choosingTarget}
        onClose={close}
        title="Select target project"
        centered
        size={400}
      >
        {folder === undefined ? null : (
          <Stack gap="md">
            <FolderPicker
              teamId={teamId}
              brandId={brandId}
              submitLabel="Delete"
              disabledIds={selfAndDescendants(tree, folder.id)}
              pending={remove.isPending}
              onSubmit={(target) => run("keep", target)}
              onCancel={close}
            />
            {remove.isError ? <ErrorAlert error={remove.error} /> : null}
          </Stack>
        )}
      </Modal>
    </>
  );
}
